import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import { FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { ConnectionService } from '../../service/connection.service';
import { SwalService } from '../../service/swal.service';
import { EventService } from '../../service/event.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IconComponent, iconSvg } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';
import { daysFromToday, todayIso } from '../../utility/share';

type WeatherBucket = 'clear' | 'cloudy' | 'fog' | 'drizzle' | 'rain' | 'snow' | 'storm';

const WEATHER_ICONS: Record<WeatherBucket, string> = {
  clear: '☀️',
  cloudy: '⛅',
  fog: '🌫️',
  drizzle: '🌦️',
  rain: '🌧️',
  snow: '❄️',
  storm: '⛈️',
};

function weatherBucket(code: number): WeatherBucket {
  if (code === 0) return 'clear';
  if ([1, 2, 3].includes(code)) return 'cloudy';
  if ([45, 48].includes(code)) return 'fog';
  if ([51, 53, 55, 56, 57].includes(code)) return 'drizzle';
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return 'rain';
  if ([71, 73, 75, 77, 85, 86].includes(code)) return 'snow';
  return 'storm';
}

function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface WeatherForecast {
  id: string;
  date: string;
  code: number;
  temp_max: number;
  temp_min: number;
}

interface Reminder {
  _id?: string;
  text: string;
  done: boolean;
}

interface CalItem {
  id: string;
  type: 'wedding' | 'test';
  name: string;
  date: string;
  time?: string;
  location?: string;
  phone?: string;
  contract_signed: boolean;
  deposit_paid: boolean;
  weather?: WeatherForecast;
  weatherIcon?: string;
  weatherTip?: string;
}

interface DayCell {
  date: string;
  day: number;
  inMonth: boolean;
  isToday: boolean;
  hasWedding: boolean;
  hasTest: boolean;
}

@Component({
  selector: 'app-calendario',
  imports: [CommonModule, FullCalendarModule, TranslatePipe, FormsModule, IconComponent, LoadErrorComponent],
  templateUrl: './calendario.component.html',
  styleUrl: './calendario.component.css',
})
export class CalendarioComponent implements OnInit, OnDestroy {

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    firstDay: 1, // Lunedì come primo giorno
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: '',
    },
    titleFormat: { year: 'numeric', month: 'long' },
    events: [],
    eventClick: this.handleEventClick.bind(this),
    dateClick: this.handleDateClick.bind(this),
    eventDidMount: (info) => {
      const loc = info.event.extendedProps?.['location'];
      let title = loc ? `${info.event.title} – ${loc}` : info.event.title;
      const type = info.event.extendedProps?.['type'];
      if (type) info.el.setAttribute('data-type', type);
      const weather = info.event.extendedProps?.['weather'] as WeatherForecast | undefined;
      if (weather) {
        const bucket = weatherBucket(weather.code);
        const icon = WEATHER_ICONS[bucket];
        const tip = this.translate.instant(`calendar.weather.${bucket}`);
        title += ` – ${tip} (${Math.round(weather.temp_max)}°/${Math.round(weather.temp_min)}°)`;
        const badge = document.createElement('span');
        badge.className = 'fc-event-weather';
        badge.textContent = icon;
        // Fuori da .fc-event-title (troncato con ellipsis), come sibling nel frame flex
        const frameEl = info.el.querySelector('.fc-event-main-frame');
        (frameEl || info.el).appendChild(badge);
      }
      info.el.setAttribute('title', title);
    },
    datesSet: (info) => this.updateMonthCount(info.view.currentStart, info.view.currentEnd),
    contentHeight: 'auto',
    height: 'auto',
    dayMaxEvents: 3,
    moreLinkText: (n) => `+${n}`,
  };

  kpiMonthCount = 0;
  kpiNextLabel = '';

  quickCreate = { open: false, top: 0, left: 0, date: '', dateLabel: '', name: '' };

  reminders: Reminder[] = [];
  // Promemoria non arrivati: niente modifiche, il salvataggio riscrive l'intera lista
  remindersError = false;
  eventsError = false;
  newReminderText = '';
  remindersOpen = true;

  typeFilter = { wedding: true, test: true };

  // ── Vista mobile (sostituisce FullCalendar sotto i 768px) ──
  isMobile = false;
  mobileView: 'month' | 'upcoming' = 'month';
  viewMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  selectedDate = todayIso();
  monthCells: DayCell[] = [];
  weekdayLabels: string[] = [];
  sheetItem: CalItem | null = null;

  private isBeautyCategory = true;
  private allMatrimoni: any[] = [];
  private allEvents: EventInput[] = [];
  private weatherByKey = new Map<string, WeatherForecast>();
  private itemsByDate = new Map<string, CalItem[]>();
  private touchStartX: number | null = null;
  private mobileQuery?: MediaQueryList;
  private onMobileChange = (e: MediaQueryListEvent) => (this.isMobile = e.matches);

  constructor(
    private router: Router,
    private connection: ConnectionService,
    private translate: TranslateService,
    private swal: SwalService,
    private ms: EventService,
  ) {}

  ngOnInit(): void {
    this.mobileQuery = window.matchMedia('(max-width: 768px)');
    this.isMobile = this.mobileQuery.matches;
    this.mobileQuery.addEventListener('change', this.onMobileChange);
    this.remindersOpen = !this.isMobile;

    const lang = this.lang;
    this.calendarOptions = {
      ...this.calendarOptions,
      locale: lang,
      buttonText: { today: this.translate.instant('calendar.m.today') },
    };
    this.weekdayLabels = Array.from({ length: 7 }, (_, i) =>
      new Date(2024, 0, 1 + i).toLocaleDateString(lang, { weekday: 'narrow' }),
    );
    this.buildMonthCells();

    this.ms.getProfile((profile: any) => {
      const cats: string[] = profile?.user?.professional_categories ?? [];
      this.isBeautyCategory = !cats.length || cats.some((c) => ['makeup', 'hair'].includes(c));
      this.rebuild();
    });

    this.loadReminders();
    this.loadCalendarData();
  }

  loadReminders(): void {
    this.connection.get(
      '@auth/reminders',
      (data: Reminder[]) => {
        this.reminders = data || [];
        this.remindersError = false;
      },
      () => (this.remindersError = true),
    );
  }

  loadCalendarData(): void {
    this.connection.get(
      'events/calendar-data',
      (data: any[]) => {
        this.allMatrimoni = data;
        this.updateNextLabel(data);
        this.updateMonthCount(this.viewMonth, this.nextMonthStart(this.viewMonth));
        this.rebuild();

        this.connection.get(
          '@events/weather-forecast',
          (weather: WeatherForecast[]) => {
            this.weatherByKey = new Map((weather ?? []).map((w) => [`${w.id}_${w.date}`, w]));
            this.rebuild();
          },
          // Meteo non disponibile: il calendario resta comunque utilizzabile senza badge.
          () => this.rebuild(),
        );
        this.eventsError = false;
      },
      () => (this.eventsError = true),
    );
  }

  ngOnDestroy(): void {
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
  }

  private get lang(): string {
    return this.translate.currentLang || this.translate.defaultLang || 'it';
  }

  get trialLabelKey(): string {
    return this.isBeautyCategory ? 'calendar.legend.test' : 'calendar.legend.sopralluogo';
  }

  private rebuild(): void {
    this.allEvents = this.buildEvents(this.allMatrimoni);
    this.applyTypeFilter();
  }

  private nextMonthStart(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }

  private updateMonthCount(start: Date, end: Date): void {
    this.kpiMonthCount = this.allMatrimoni.filter((m) => {
      if (!m.data || m.is_quote) return false;
      const d = new Date(`${String(m.data).slice(0, 10)}T00:00:00`);
      return d >= start && d < end;
    }).length;
  }

  private updateNextLabel(matrimoni: any[]): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextDates = matrimoni
      .filter((m) => !m.is_quote)
      .map((m) => (m.data ? new Date(`${String(m.data).slice(0, 10)}T00:00:00`) : null))
      .filter((d): d is Date => !!d && d >= today)
      .sort((a, b) => a.getTime() - b.getTime());

    if (!nextDates.length) {
      this.kpiNextLabel = this.translate.instant('calendar.kpi.next_none');
      return;
    }
    const days = Math.round((nextDates[0].getTime() - today.getTime()) / 86400000);
    if (days === 0) this.kpiNextLabel = this.translate.instant('calendar.kpi.next_today');
    else if (days === 1) this.kpiNextLabel = this.translate.instant('calendar.kpi.next_tomorrow');
    else this.kpiNextLabel = this.translate.instant('calendar.kpi.next_in_days', { count: days });
  }

  private buildEvents(matrimoni: any[]): EventInput[] {
    const trialLabel = this.translate.instant(this.trialLabelKey);
    return matrimoni.flatMap((m) => {
      const shared = {
        id: m.id,
        name: m.nome,
        location: m.location,
        time: m.time,
        contract_signed: m.contract_signed,
        deposit_paid: m.deposit_paid,
      };
      // Un preventivo senza data precisa resta fuori dal calendario
      const events: EventInput[] = m.data
        ? [
            {
              title: m.nome,
              start: m.data,
              color: 'var(--rose)',
              classNames: m.is_quote ? ['is-quote'] : [],
              extendedProps: {
                ...shared,
                type: 'wedding',
                weather: this.weatherByKey.get(`${m.id}_${m.data}`),
              },
            },
          ]
        : [];
      if (m.test_date) {
        const dates = Array.isArray(m.test_date) ? m.test_date : [m.test_date];
        dates.forEach((date: string) => {
          events.push({
            title: `${trialLabel} · ${m.nome}`,
            start: date,
            color: 'var(--gold)',
            extendedProps: {
              ...shared,
              location: m.test_location || m.location,
              time: m.test_time,
              type: 'test',
              weather: this.weatherByKey.get(`${m.id}_${date}`),
            },
          });
        });
      }
      return events;
    });
  }

  private applyTypeFilter(): void {
    const visible = this.allEvents.filter((e) => {
      const type = e.extendedProps?.['type'];
      return type === 'test' ? this.typeFilter.test : this.typeFilter.wedding;
    });
    this.calendarOptions = { ...this.calendarOptions, events: visible };
    this.buildItems(visible);
  }

  toggleTypeFilter(type: 'wedding' | 'test'): void {
    this.typeFilter[type] = !this.typeFilter[type];
    this.applyTypeFilter();
  }

  // ── MOBILE: mese a puntini + agenda del giorno ──────────────────────────────

  private buildItems(events: EventInput[]): void {
    const phoneById = new Map(this.allMatrimoni.map((m) => [String(m.id), m.phone]));
    const byDate = new Map<string, CalItem[]>();
    for (const e of events) {
      const p: any = e.extendedProps ?? {};
      const date = String(e.start ?? '').slice(0, 10);
      if (!date) continue;
      const weather = p.weather as WeatherForecast | undefined;
      const bucket = weather ? weatherBucket(weather.code) : null;
      const item: CalItem = {
        id: String(p.id),
        type: p.type,
        name: p.name ?? String(e.title),
        date,
        time: p.time,
        location: p.location,
        phone: phoneById.get(String(p.id)),
        contract_signed: !!p.contract_signed,
        deposit_paid: !!p.deposit_paid,
        weather,
        weatherIcon: bucket ? WEATHER_ICONS[bucket] : undefined,
        weatherTip: bucket ? this.translate.instant(`calendar.weather.${bucket}`) : undefined,
      };
      const list = byDate.get(date) ?? [];
      list.push(item);
      byDate.set(date, list);
    }
    byDate.forEach((list) => list.sort((a, b) => (a.time ?? '99').localeCompare(b.time ?? '99')));
    this.itemsByDate = byDate;
    this.buildMonthCells();
  }

  private buildMonthCells(): void {
    const first = this.viewMonth;
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const total = Math.ceil((offset + daysInMonth) / 7) * 7;
    const today = todayIso();

    this.monthCells = Array.from({ length: total }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      const date = isoDate(d);
      const items = this.itemsByDate.get(date) ?? [];
      return {
        date,
        day: d.getDate(),
        inMonth: d.getMonth() === first.getMonth(),
        isToday: date === today,
        hasWedding: items.some((it) => it.type === 'wedding'),
        hasTest: items.some((it) => it.type === 'test'),
      };
    });
  }

  get monthTitle(): string {
    const label = this.viewMonth.toLocaleDateString(this.lang, { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  get selectedItems(): CalItem[] {
    return this.itemsByDate.get(this.selectedDate) ?? [];
  }

  get selectedLabel(): string {
    return this.relativeDayLabel(this.selectedDate);
  }

  get upcomingGroups(): { date: string; label: string; items: CalItem[] }[] {
    const today = todayIso();
    const limit = isoDate(new Date(Date.now() + 90 * 86400000));
    return Array.from(this.itemsByDate.keys())
      .filter((d) => d >= today && d <= limit)
      .sort()
      .map((date) => ({ date, label: this.relativeDayLabel(date), items: this.itemsByDate.get(date)! }));
  }

  get openRemindersCount(): number {
    return this.reminders.filter((r) => !r.done).length;
  }

  private relativeDayLabel(date: string): string {
    const days = daysFromToday(date);
    if (days === 0) return this.translate.instant('calendar.m.today');
    if (days === 1) return this.translate.instant('calendar.m.tomorrow');
    const label = new Date(`${date}T00:00:00`).toLocaleDateString(this.lang, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  itemLabelKey(item: CalItem): string {
    return item.type === 'test' ? this.trialLabelKey : 'calendar.legend.wedding';
  }

  selectDate(date: string): void {
    this.selectedDate = date;
    const d = new Date(`${date}T00:00:00`);
    if (d.getMonth() !== this.viewMonth.getMonth() || d.getFullYear() !== this.viewMonth.getFullYear()) {
      this.setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }

  private setViewMonth(d: Date): void {
    this.viewMonth = d;
    this.buildMonthCells();
    this.updateMonthCount(d, this.nextMonthStart(d));
  }

  shiftMonth(delta: number): void {
    const d = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + delta, 1);
    this.setViewMonth(d);
    this.selectedDate = isoDate(d);
  }

  goToday(): void {
    this.mobileView = 'month';
    this.selectDate(todayIso());
  }

  onTouchStart(e: TouchEvent): void {
    this.touchStartX = e.changedTouches[0]?.clientX ?? null;
  }

  onTouchEnd(e: TouchEvent): void {
    if (this.touchStartX === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? this.touchStartX) - this.touchStartX;
    this.touchStartX = null;
    if (Math.abs(dx) > 60) this.shiftMonth(dx < 0 ? 1 : -1);
  }

  openSheet(item: CalItem): void {
    this.sheetItem = item;
  }

  closeSheet(): void {
    this.sheetItem = null;
  }

  sheetDateLabel(item: CalItem): string {
    return this.formatDateLabel(item.date);
  }

  openEvent(id: string): void {
    this.sheetItem = null;
    this.router.navigate(['/evento', id]);
  }

  openDirections(location?: string): void {
    if (!location?.trim()) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.trim())}`, '_blank', 'noopener');
  }

  openQuickCreateFor(date: string): void {
    this.quickCreate = { open: true, top: 0, left: 0, date, dateLabel: this.formatDateLabel(date), name: '' };
  }

  // ── DESKTOP (FullCalendar) ──────────────────────────────────────────────────

  async handleEventClick(info: any): Promise<void> {
    await this.openEventSummary(info.event);
  }

  private async openEventSummary(event: any): Promise<void> {
    const props = event.extendedProps;
    const dateLabel = this.formatDateLabel(event.startStr);

    const lines: string[] = [];
    lines.push(
      `<p style="margin:0 0 .5rem;"><strong>${this.translate.instant('calendar.modal.date')}:</strong> ${dateLabel}${props.time ? ' · ' + props.time : ''}</p>`,
    );
    if (props.location) {
      lines.push(`<p style="margin:0 0 .5rem;">${iconSvg('pin')} ${props.location}</p>`);
    }
    const weather = props.weather as WeatherForecast | undefined;
    if (weather) {
      const bucket = weatherBucket(weather.code);
      const icon = WEATHER_ICONS[bucket];
      const tip = this.translate.instant(`calendar.weather.${bucket}`);
      lines.push(
        `<p style="margin:0 0 .5rem;">${icon} ${tip} (${Math.round(weather.temp_max)}°/${Math.round(weather.temp_min)}°)</p>`,
      );
    }
    const pill = (ok: boolean, okKey: string, pendingKey: string) =>
      `<span style="display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border-radius:20px; font-size:.8rem; background:${ok ? 'var(--gold-light)' : 'var(--rose-light)'}; color:${ok ? 'var(--gold)' : 'var(--rose)'};">${iconSvg(ok ? 'check' : 'hourglass', 13)} ${this.translate.instant(ok ? okKey : pendingKey)}</span>`;
    lines.push(
      `<div style="display:flex; gap:.5rem; margin-top:.75rem; flex-wrap:wrap;">` +
        pill(!!props.contract_signed, 'calendar.modal.contract_signed', 'calendar.modal.contract_pending') +
        pill(!!props.deposit_paid, 'calendar.modal.deposit_paid', 'calendar.modal.deposit_pending') +
        `</div>`,
    );

    const html = `<div style="text-align:left; text-transform:none !important; font-size:.95rem;">${lines.join('')}</div>`;

    const result = await this.swal.confirmHtml(
      event.title,
      html,
      this.translate.instant('calendar.modal.open_event'),
      this.translate.instant('calendar.modal.close'),
      null,
    );
    if (result.isConfirmed) {
      this.router.navigate(['/evento', props.id]);
    }
  }

  handleDateClick(info: any): void {
    const rect = info.dayEl.getBoundingClientRect();
    const popoverWidth = 260;
    const popoverHeight = 150;
    let left = rect.left + rect.width / 2 - popoverWidth / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - popoverWidth - 12));
    let top = rect.bottom + 8;
    if (top + popoverHeight > window.innerHeight) {
      top = Math.max(12, rect.top - popoverHeight - 8);
    }
    const dateLabel = this.formatDateLabel(info.dateStr);
    this.quickCreate = { open: true, top, left, date: info.dateStr, dateLabel, name: '' };
  }

  closeQuickCreate(): void {
    this.quickCreate.open = false;
  }

  submitQuickCreate(): void {
    const name = this.quickCreate.name.trim();
    if (!name) return;
    this.quickCreate.open = false;
    this.router.navigate(['/eventi/nuovo'], {
      queryParams: { date: this.quickCreate.date, name },
    });
  }

  private formatDateLabel(dateStr: string): string {
    return new Date(`${dateStr.slice(0, 10)}T00:00:00`).toLocaleDateString(this.lang, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  // ── PROMEMORIA (checklist libera, senza data) ────────────────────────────

  private saveReminders(): void {
    if (this.remindersError) return;
    this.connection.put(
      '@auth/reminders',
      { reminders: this.reminders },
      (data: Reminder[]) => (this.reminders = data || this.reminders),
      (error: any) => console.error(error),
    );
  }

  addReminder(): void {
    const text = this.newReminderText.trim();
    if (!text) return;
    this.reminders = [...this.reminders, { text, done: false }];
    this.newReminderText = '';
    this.saveReminders();
  }

  toggleReminder(reminder: Reminder): void {
    reminder.done = !reminder.done;
    this.saveReminders();
  }

  removeReminder(reminder: Reminder): void {
    this.reminders = this.reminders.filter((r) => r !== reminder);
    this.saveReminders();
  }
}
